/* mt-backed.js — 修复版
   - 单文件：包含类 MTBacked，幂等初始化，浏览器策略回退（静音重试播放）
   - 使用说明：直接引入到页面或 theme assets 中即可
*/

class MTBacked {
  constructor() {
    // make instance idempotent in case constructor called multiple times
    if (MTBacked._initialized) {
      return MTBacked._instance;
    }
    MTBacked._initialized = true;
    MTBacked._instance = this;

    // SVG icons used throughout
    this.playSVG = '<svg width="60" height="60" viewBox="0 0 60 60" fill="none" aria-hidden="true"><circle cx="30" cy="30" r="30" fill="white" fill-opacity="0.9"/><path d="M24 18L40 30L24 42V18Z" fill="black"/></svg>';
    this.pauseSVG = '<svg width="60" height="60" viewBox="0 0 60 60" fill="none" aria-hidden="true"><circle cx="30" cy="30" r="30" fill="white" fill-opacity="0.9"/><rect x="22" y="18" width="6" height="24" rx="1" fill="black"/><rect x="32" y="18" width="6" height="24" rx="1" fill="black"/></svg>';

    // init flow
    this.init();
  }

  init() {
    // cache modal element if present
    this.modal = document.getElementById('testimonialModal');

    // setup parts
    this.setupVideoPlayers();
    // this.setupExpandButtons();
    this.setupModal();

    // ensure we pause videos when navigating away (optional safety)
    window.addEventListener('pagehide', () => {
      this.pauseAllVideos();
    });
  }

  // safe play helper: tries video.play(), if rejected tries mute -> play -> restore muted state
  async safePlay(video) {
    if (!video) return;
    try {
      await video.play();
    } catch (err) {
      // try muting and re-trying once (helps on some mobile/auto-play policies)
      try {
        const wasMuted = video.muted;
        video.muted = true;
        await video.play();
        // restore original muted after a small delay to allow audible resume if desired
        setTimeout(() => {
          video.muted = wasMuted;
        }, 200);
      } catch (err2) {
        // still failed — nothing to do; calling code may show UI feedback if wanted
      }
    }
  }

  pauseAllVideos() {
    document.querySelectorAll('.mt-backed-video-wrapper video').forEach(v => {
      try { v.pause(); } catch (e) {}
    });
  }

  setupVideoPlayers() {
    const wrappers = document.querySelectorAll('.mt-backed-video-wrapper');

    wrappers.forEach(wrapper => {
      // prevent double init of same wrapper
      if (wrapper.dataset.mtInited === 'true') return;
      wrapper.dataset.mtInited = 'true';

      const video = wrapper.querySelector('video');
      const playBtn = wrapper.querySelector('.mt-backed-play-btn');

      if (!video || !playBtn) {
        // still set dataset to avoid retrying excessively
        return;
      }

      // Ensure attributes for mobile inline playback
      video.setAttribute('playsinline', '');
      video.setAttribute('preload', video.getAttribute('preload') || 'metadata');

      // Accessibility
      playBtn.setAttribute('role', 'button');
      playBtn.setAttribute('tabindex', playBtn.getAttribute('tabindex') || '0');
      playBtn.setAttribute('aria-label', '播放/暂停 视频');
      playBtn.setAttribute('aria-pressed', 'false');

      // initialize icon
      playBtn.innerHTML = this.playSVG;

      // local helpers
      const updateButtonIcon = () => {
        if (video.paused) {
          playBtn.innerHTML = this.playSVG;
          playBtn.setAttribute('aria-pressed', 'false');
        } else {
          playBtn.innerHTML = this.pauseSVG;
          playBtn.setAttribute('aria-pressed', 'true');
        }
      };

      const onPlay = () => {
        wrapper.classList.add('playing');
        updateButtonIcon();
      };
      const onPause = () => {
        wrapper.classList.remove('playing');
        updateButtonIcon();
      };

      // button click toggles play/pause (stopPropagation so wrapper click won't double-run)
      const onBtnClick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (video.paused) {
          // pause others first
          document.querySelectorAll('.mt-backed-video-wrapper video').forEach(v => {
            if (v !== video) {
              try { v.pause(); } catch (err) {}
            }
          });
          this.safePlay(video);
        } else {
          try { video.pause(); } catch (err) {}
        }
      };

      // keyboard support on button (Enter / Space)
      const onBtnKey = (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onBtnClick(e);
        }
      };

      // clicking wrapper also toggles (but ignore clicks originated from the button)
      const onWrapperClick = (e) => {
        if (playBtn.contains(e.target)) return; // button click already handled
        if (video.paused) {
          // pause others
          document.querySelectorAll('.mt-backed-video-wrapper video').forEach(v => {
            if (v !== video) {
              try { v.pause(); } catch (err) {}
            }
          });
          this.safePlay(video);
        } else {
          try { video.pause(); } catch (err) {}
        }
      };

      // touch support: when playing, short touch will reveal button so user can pause
      const onTouchStart = () => {
        if (wrapper.classList.contains('playing')) {
          playBtn.style.opacity = '1';
          playBtn.style.pointerEvents = 'auto';
          setTimeout(() => {
            playBtn.style.opacity = '';
            playBtn.style.pointerEvents = '';
          }, 1600);
        }
      };

      // event binding (make sure we don't bind twice because of data flag)
      playBtn.addEventListener('click', onBtnClick, { passive: false });
      playBtn.addEventListener('keydown', onBtnKey);
      wrapper.addEventListener('click', onWrapperClick);
      wrapper.addEventListener('touchstart', onTouchStart, { passive: true });

      // video events update UI
      video.addEventListener('play', onPlay);
      video.addEventListener('pause', onPause);
      video.addEventListener('ended', onPause);

      // ensure initial button state matches video state
      updateButtonIcon();
    });
  }

  setupExpandButtons() {
    // classical pattern: find expand buttons and open modal with testimonial content
    const expandButtons = document.querySelectorAll('.mt-backed-expand-btn');

    expandButtons.forEach(button => {
      if (button.dataset.mtExpandInited === 'true') return;
      button.dataset.mtExpandInited = 'true';

      button.addEventListener('click', (e) => {
        e.preventDefault();
        const testimonialCard = button.closest('.mt-backed-testimonial');
        if (!testimonialCard) return;

        const quoteEl = testimonialCard.querySelector('.mt-backed-quote');
        const authorNameEl = testimonialCard.querySelector('.mt-backed-author-name');
        const authorRoleEl = testimonialCard.querySelector('.mt-backed-author-role');

        const quote = quoteEl ? quoteEl.textContent.trim() : '';
        const authorName = authorNameEl ? authorNameEl.textContent.trim() : '';
        const authorRole = authorRoleEl ? authorRoleEl.textContent.trim() : '';

        this.openModal(quote, authorName, authorRole);
      });
    });
  }

  setupModal() {
    if (!this.modal) return;

    // avoid binding multiple times
    if (this.modal.dataset.mtModalInited === 'true') return;
    this.modal.dataset.mtModalInited = 'true';

    const overlay = this.modal.querySelector('.mt-backed-modal-overlay');
    const closeBtn = this.modal.querySelector('.mt-backed-modal-close');

    if (overlay) {
      overlay.addEventListener('click', () => {
        this.closeModal();
      });
    }
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.closeModal();
      });
    }

    // ESC close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        this.closeModal();
      }
    });
  }

  openModal(quote, authorName, authorRole) {
    if (!this.modal) return;
    const modalQuote = this.modal.querySelector('.mt-backed-modal-quote');
    const modalAuthorName = this.modal.querySelector('.mt-backed-modal-author-name');
    const modalAuthorRole = this.modal.querySelector('.mt-backed-modal-author-role');

    if (modalQuote) modalQuote.textContent = quote;
    if (modalAuthorName) modalAuthorName.textContent = authorName;
    if (modalAuthorRole) modalAuthorRole.textContent = authorRole;

    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';

    // pause all videos when modal opens
    this.pauseAllVideos();
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.classList.remove('active');
    document.body.style.overflow = '';
  }
}

/* -------------------------
   Auto-init on DOM ready
   ------------------------- */
(function () {
  function initOnce() {
    try {
      new MTBacked();
    } catch (err) {
      // swallow to avoid breaking other scripts
      console.error('MTBacked init error:', err);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOnce);
  } else {
    initOnce();
  }

  // Shopify theme editor: when sections load/unload, run init (elements are guarded by data-mt-inited)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', initOnce);
    // optional: when section unloads, we don't need to do anything because dataset avoids double inits.
  }



   const expandButtons = document.querySelectorAll('.mt-backed-expand-btn');
    expandButtons.forEach(button => {
      button.addEventListener('click',()=>{
        button.classList.toggle("active");
      })
    });

})();
